/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react/no-array-index-key */
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Grid,
  LinearProgress,
  makeStyles,
  Toolbar,
  Typography
} from "@material-ui/core";
import { useQuery } from "@redux-requests/react";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import InfiniteScroll from "react-infinite-scroll-component";
import { useIntl } from "react-intl";
import { Formik } from "formik";
import { isEqual as _isEqual, uniqWith as _uniqWith } from "lodash";
import { Header, Loader, OverflowTip, PdfViewerModal } from "../../components";
import { ReactComponent as CertificatesSvg } from "../../assets/icons/certificates.svg";
import { getCertificates } from "../../ducks/actions/certificates";
import * as types from "../../ducks/types";
import { SelectField } from "../../components/formik";

const useStyles = makeStyles(() => ({
  infiniteScroll: {
    overflow: "hidden !important"
  },
  gridContainer: {
    paddingTop: "1rem",
    paddingBottom: "1rem",
    width: "100%"
  },
  filterContainer: {
    paddingTop: "1rem",
    paddingBottom: "1rem",
    justifyContent: "space-between",
    width: "100%"
  },
  contentCard: {
    height: "128px"
  },
  actionCard: {
    justifyContent: "space-around"
  },
  filterToolbar: {
    marginTop: "1rem"
  }
}));

const CertificatesPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const classes = useStyles();

  const [perPage] = useState(12);
  const [page, setPage] = useState(1);
  const [courseOptions, setCourseOptions] = useState([]);
  const [courseIdFilter, setCourseIdFilter] = useState("All");

  const [certificateModalState, setCertificateModalState] = useState({
    certificateLink: null,
    modalOpen: false
  });

  const {
    data: {
      certificates,
      meta: { total: totalCertificates }
    },
    loading: certificatesLoading
  } = useQuery({
    type: types.GET_CERTIFICATES,
    defaultData: { certificates: [], meta: { total: 0 } }
  });

  useEffect(() => {
    dispatch(
      getCertificates({
        page,
        perPage,
        courseIdFilter: courseIdFilter === "All" ? undefined : courseIdFilter
      })
    );
  }, [dispatch, page, perPage, courseIdFilter]);

  useEffect(() => {
    if (courseIdFilter === "All") {
      setCourseOptions([
        { label: "Tutti i Corsi", value: "All" },
        ..._uniqWith(
          certificates.map(({ course }) => ({
            id: course.id,
            name: course.name
          })),
          _isEqual
        ).map(({ id, name }) => ({
          label: name,
          value: id
        }))
      ]);
    }
  }, [dispatch, certificates, courseIdFilter]);

  const nextCertificatesPage = () => {
    setPage(page + 1);
  };

  const openCertificate = pdfUrl => () => {
    setCertificateModalState({ certificateLink: pdfUrl, modalOpen: true });
  };

  const closeCertificate = () => {
    setCertificateModalState({ certificateLink: null, modalOpen: false });
  };

  const searchCertificate = ({ courseId }) => {
    setCourseIdFilter(courseId);
    setPage(1);
  };

  return (
    <>
      <InfiniteScroll
        className={classes.infiniteScroll}
        scrollableTarget="scrollerContent"
        dataLength={certificates.length}
        next={nextCertificatesPage}
        hasMore={certificates.length < totalCertificates}
        loader={<LinearProgress />}
        endMessage={
          <p style={{ textAlign: "center" }}>
            <b>
              {intl.formatMessage({
                id: "Pages.Certificates.InfiniteScrollEnd",
                defaultMessage: "All the cretificates are shown!"
              })}
            </b>
          </p>
        }
      >
        <Header
          icon={CertificatesSvg}
          title={intl.formatMessage({
            id: "Pages.Certificates.Certificates",
            defaultMessage: "Certificates"
          })}
        />
        <Loader loading={certificatesLoading}>
          <Formik
            onSubmit={searchCertificate}
            initialValues={{
              courseId: courseIdFilter || ""
            }}
          >
            <Toolbar className={classes.filterToolbar}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={2} lg={1}>
                  <Typography variant="subtitle1">
                    <strong>
                      {intl.formatMessage({
                        id: "Pages.Certificates.FilterBy",
                        defaultMessage: "Filter by"
                      })}
                    </strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} lg={4} xl={3}>
                  <SelectField
                    name="courseId"
                    variant="outlined"
                    label={intl.formatMessage({
                      id: "Pages.Certificates.SelectLabel",
                      defaultMessage: "Id of the Course"
                    })}
                    options={courseOptions}
                    submitOnChange
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Toolbar>
          </Formik>

          <Grid container className={classes.gridContainer} spacing={3}>
            {certificates.map(({ pdf, created_at: createdAt, course }, index) => (
              <Grid key={index} item xs={12} sm={4} md={3} lg={3}>
                <Card className={classes.card}>
                  <CardMedia
                    component="img"
                    height="100%"
                    objectfit="cover"
                    image={pdf.preview_url}
                  />
                  <CardContent className={classes.contentCard}>
                    <Typography variant="overline" display="block" color="textPrimary">
                      Conseguito il: {moment(createdAt).format("DD/MM/YYYY")}
                    </Typography>
                    <OverflowTip
                      textToDisplay={`Corso: ${course.name}`}
                      toolipText={course.name}
                      textStyle={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical"
                      }}
                      textVariant="overline"
                      display="block"
                      color="text-primary"
                    />
                  </CardContent>
                  <CardActions className={classes.actionCard}>
                    <Button size="small" color="primary" onClick={openCertificate(pdf.url)}>
                      Visualizza Online
                    </Button>
                    <Button href={pdf.url} download target="_blank" size="small" color="primary">
                      Download
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
          <PdfViewerModal
            isOpen={certificateModalState.modalOpen}
            documentUrl={certificateModalState.certificateLink}
            handleClose={closeCertificate}
          />
        </Loader>
      </InfiniteScroll>
    </>
  );
};

export default CertificatesPage;
